// Design system da tela "Templates de mensagens" — full-bleed, plano e com
// bordas hairline. Cores do tema da loja (theme.colors) → acompanha a primária
// personalizada e claro/escuro. Convenção: página = background; painéis/cards =
// backgroundSecondary; encaixes internos = background; borda = lightGrayOpacity.
const border = (theme) => `1px solid ${theme.colors.lightGrayOpacity}`;

// Altura do miolo (3 colunas) descontando o chrome (navbar do app + header
// compacto + tabs + barra de filtros). Ligeiramente generoso pra nunca gerar
// scroll na página — os painéis rolam por dentro.
const PANES_H = 'calc(100vh - 208px)';

export const styles = {
  page: (theme) => ({ minHeight: '100vh', background: theme.colors.background }),

  // Header compacto (não-sticky: a navbar do app já é sticky no topo). Sem
  // subtítulo — as tabs dão o contexto — pra economizar altura.
  topbar: (theme) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
    padding: '9px clamp(16px, 3vw, 32px)',
    background: theme.colors.backgroundSecondary,
  }),
  logo: (theme) => ({ width: 30, height: 30, borderRadius: 7, background: theme.colors.primary, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }),
  title: (theme) => ({ margin: 0, fontSize: 16, fontWeight: 700, letterSpacing: -0.2, color: theme.colors.text }),
  topActions: { marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' },

  // Tabs (mesmo bloco visual do header)
  tabsBar: (theme) => ({ display: 'flex', gap: 4, padding: '0 clamp(16px, 3vw, 32px)', background: theme.colors.backgroundSecondary, borderBottom: border(theme) }),
  tab: (theme, active) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    border: 'none',
    borderBottom: `2px solid ${active ? theme.colors.primary : 'transparent'}`,
    background: 'transparent',
    color: active ? theme.colors.text : theme.colors.textSecondary,
    fontFamily: 'inherit',
    fontSize: 13.5,
    fontWeight: active ? 700 : 600,
    padding: '9px 12px',
    cursor: 'pointer',
    marginBottom: -1,
  }),

  // Selo "Em breve" na tab de recurso ainda não liberado pro contrato
  soonBadge: (theme) => ({ marginLeft: 2, padding: '1px 6px', borderRadius: 6, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: theme.colors.warning, background: `${theme.colors.warning}22` }),

  body: { padding: '12px clamp(16px, 3vw, 32px) 0' },

  // Toolbar (tab Oficiais)
  toolbar: { display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginBottom: 18 },
  toolbarInfo: (theme) => ({ fontSize: 13, color: theme.colors.textSecondary }),
  toolbarActions: { marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' },

  // Callout
  callout: (theme) => ({ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', borderRadius: 10, marginBottom: 18, border: border(theme), background: theme.colors.backgroundSecondary, flexWrap: 'wrap' }),
  calloutIcon: (theme) => ({ width: 36, height: 36, borderRadius: 8, background: `${theme.colors.primary}18`, color: theme.colors.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: `1px solid ${theme.colors.primary}44` }),
  calloutBody: { flex: '1 1 280px', minWidth: 240 },
  calloutTitle: (theme) => ({ fontSize: 14, fontWeight: 700, color: theme.colors.text, marginBottom: 1 }),
  calloutText: (theme) => ({ fontSize: 12.5, color: theme.colors.textSecondary, lineHeight: 1.45 }),

  // Filtros
  filters: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 16 },
  filterBar: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' },
  searchWrap: { maxWidth: 260, flex: '1 1 200px' },

  segbar: (theme) => ({ display: 'inline-flex', border: border(theme), borderRadius: 8, overflow: 'hidden' }),
  seg: (theme, active) => ({ border: 'none', borderRight: border(theme), background: active ? `${theme.colors.primary}14` : theme.colors.backgroundSecondary, color: active ? theme.colors.primary : theme.colors.textSecondary, fontFamily: 'inherit', fontSize: 12.5, fontWeight: 600, padding: '7px 12px', cursor: 'pointer' }),

  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(310px, 1fr))', gap: 14, alignItems: 'start' },

  emptyState: { padding: '52px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 12 },
  emptyIcon: (theme) => ({ width: 60, height: 60, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.colors.primary, background: `${theme.colors.primary}14` }),
  emptyText: (theme) => ({ margin: 0, maxWidth: 400, fontSize: 14, lineHeight: 1.5, color: theme.colors.textSecondary }),

  // ── Tab Sistema: barra de filtros + 3 colunas ──
  sysBar: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 },
  panes: { display: 'flex', gap: 12, height: PANES_H, minHeight: 400 },

  // Coluna 1 — lista (lista de verdade: linhas rentes com divisória)
  listPane: (theme) => ({ flex: '0 0 340px', display: 'flex', flexDirection: 'column', background: theme.colors.backgroundSecondary, border: border(theme), borderRadius: 10, overflow: 'hidden' }),
  listHead: (theme) => ({ padding: 8, borderBottom: border(theme) }),
  listScroll: { flex: 1, overflowY: 'auto' },
  listEmpty: (theme) => ({ padding: '22px 12px', textAlign: 'center', fontSize: 12.5, color: theme.colors.textSecondary }),
  msgRow: (theme, selected) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 9,
    padding: '9px 11px',
    cursor: 'pointer',
    borderBottom: border(theme),
    background: selected ? `${theme.colors.primary}12` : 'transparent',
    boxShadow: selected ? `inset 2px 0 0 ${theme.colors.primary}` : 'none',
  }),
  rowDot: (accent) => ({ width: 8, height: 8, borderRadius: 3, background: accent, flexShrink: 0 }),
  rowName: (theme, off) => ({ fontSize: 13, fontWeight: 600, color: theme.colors.text, opacity: off ? 0.5 : 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
  rowType: (theme) => ({ fontSize: 11, color: theme.colors.textSecondary, marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
  starBtn: (active) => ({ border: 'none', background: 'transparent', cursor: 'pointer', padding: 2, color: active ? '#E9A70C' : '#c3cad4', display: 'flex', flexShrink: 0 }),

  // Coluna 2 — prévia (não cresce além do celular; largura ~fixa)
  previewPane: (theme) => ({ flex: '0 1 380px', minWidth: 300, background: theme.colors.background, border: border(theme), borderRadius: 10, padding: 16, overflowY: 'auto' }),
  previewHead: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' },
  previewLabel: (theme) => ({ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: theme.colors.textSecondary }),
  scenarioSelect: (theme) => ({ marginLeft: 'auto', padding: '5px 9px', borderRadius: 7, border: border(theme), background: theme.colors.backgroundSecondary, color: theme.colors.text, fontSize: 12 }),
  phoneWrap: { maxWidth: 340, margin: '0 auto' },

  // Coluna 3 — edição (ocupa a maior parte do espaço)
  formPane: (theme) => ({ flex: '1 1 400px', minWidth: 340, display: 'flex', flexDirection: 'column', background: theme.colors.backgroundSecondary, border: border(theme), borderRadius: 10, overflow: 'hidden' }),
  formScroll: { flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 15 },
  formFooter: (theme) => ({ flexShrink: 0, display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '11px 16px', borderTop: border(theme), background: theme.colors.backgroundSecondary }),

  // Placeholder das colunas quando nada selecionado
  paneEmpty: (theme) => ({ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: 10, padding: 24, fontSize: 13, color: theme.colors.textSecondary }),
  paneEmptyIcon: (theme) => ({ width: 50, height: 50, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.colors.primary, background: `${theme.colors.primary}12` }),

  // Campos do editor
  editorNote: (theme) => ({ fontSize: 12.5, padding: '9px 11px', borderRadius: 8, background: theme.colors.background, color: theme.colors.textSecondary, border: border(theme), lineHeight: 1.45 }),
  fieldLabel: (theme) => ({ fontSize: 12.5, fontWeight: 650, color: theme.colors.text, marginBottom: 6, display: 'block' }),
  fieldHint: (theme) => ({ fontSize: 12, color: theme.colors.textSecondary, margin: '0 0 8px', lineHeight: 1.45 }),
  checkRow: { display: 'flex', gap: 18, flexWrap: 'wrap' },
  chips: { display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 8 },
  chip: (theme) => ({ cursor: 'pointer', border: `1px solid ${theme.colors.primary}44`, background: `${theme.colors.primary}10`, color: theme.colors.primary, fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 6 }),

  // Seções do resumo
  resumeSection: (theme) => ({ borderRadius: 8, background: theme.colors.background, border: border(theme), padding: 12, marginBottom: 10 }),
  resumeSectionHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 4 },
  resumeSectionTitle: (theme) => ({ fontSize: 13, fontWeight: 700, color: theme.colors.text, display: 'flex', alignItems: 'center', gap: 6 }),
  resumeBadge: (theme, conditional) => ({ fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 6, color: conditional ? theme.colors.warning : theme.colors.success, background: conditional ? `${theme.colors.warning}22` : `${theme.colors.success}22` }),
  resumeSectionDesc: (theme) => ({ fontSize: 11.5, color: theme.colors.textSecondary, margin: '0 0 8px', lineHeight: 1.45 }),

  // Config modal
  configIntro: (theme) => ({ margin: '0 0 16px', fontSize: 13.5, lineHeight: 1.5, color: theme.colors.textSecondary }),
  configCard: (theme, active) => ({ display: 'flex', alignItems: 'flex-start', gap: 12, padding: 14, borderRadius: 10, marginBottom: 10, background: theme.colors.background, border: `1px solid ${active ? `${theme.colors.primary}44` : theme.colors.lightGrayOpacity}` }),
  configIcon: (theme, active) => ({ width: 34, height: 34, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: active ? '#fff' : theme.colors.textSecondary, background: active ? theme.colors.primary : theme.colors.backgroundSecondary, border: active ? 'none' : border(theme) }),
  configLabel: (theme) => ({ fontSize: 14, fontWeight: 600, color: theme.colors.text, marginBottom: 3 }),
  configDesc: (theme) => ({ fontSize: 12.5, lineHeight: 1.45, color: theme.colors.textSecondary }),
  costHint: (theme) => ({ display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 4, padding: '10px 12px', borderRadius: 8, fontSize: 12.5, lineHeight: 1.5, color: theme.colors.text, background: `${theme.colors.primary}14` }),
  costHintIcon: (theme) => ({ width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#fff', background: theme.colors.primary }),
  drawerField: { marginBottom: 20 },
};
